import { useState } from "react";
import { type EntityType, type Member, type Permission, type Role, api } from "../lib/api.ts";
import { readFailure } from "../lib/read-failure.ts";
import { useLoad } from "../lib/useLoad.ts";
/**
 * Platform-side access: our own operators and what they hold.
 *
 * The role ladder is drawn as a ladder because it is one — each rung strictly
 * contains the one below, and where you sit decides whether the grant editor is
 * relevant to you at all. admin and above hold everything by role, so
 * per-entity grants only exist for manager and user.
 */
import { Badge, Button, Card, InfoBanner, Input, Page, Select, Table } from "../ui/index.ts";

const LADDER: Array<{ role: Role; holds: string }> = [
	{ role: "user", holds: "Reads only the agents, ad accounts and proxies granted to them." },
	{ role: "manager", holds: "Reads and changes what is granted to them, instance by instance." },
	{ role: "admin", holds: "Every entity. Seats members up to manager." },
	{ role: "super_admin", holds: "Every entity. Seats members up to super_admin." },
];

const TYPES: Array<{ type: EntityType; label: string }> = [
	{ type: "tiktok_accounts", label: "Ad accounts" },
	{ type: "agents", label: "Agents" },
	{ type: "proxies", label: "Proxies" },
];

export function Access() {
	const [mine] = useLoad(() => api.myAccess());
	const [members, reloadMembers] = useLoad(() => api.members());
	const [accounts] = useLoad(() => api.tiktokAccounts());
	const [agents] = useLoad(() => api.agents());
	const [proxies] = useLoad(() => api.proxies());
	const [editing, setEditing] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const myRole = mine.state === "ready" ? mine.data.role : null;
	const canAssign = mine.state === "ready" ? mine.data.canAssign : [];

	const options: Record<EntityType, Array<{ id: string; label: string }>> = {
		tiktok_accounts:
			accounts.state === "ready"
				? accounts.data.accounts.map((a) => ({ id: a.id, label: `${a.name} · ${a.advertiserId}` }))
				: [],
		agents:
			agents.state === "ready"
				? agents.data.agents.map((a) => ({ id: a.id, label: `${a.name} · ${a.kind}` }))
				: [],
		proxies:
			proxies.state === "ready"
				? proxies.data.proxies.map((p) => ({ id: p.id, label: `${p.label} · ${p.host}:${p.port}` }))
				: [],
	};

	async function run(action: () => Promise<unknown>) {
		setBusy(true);
		setError(null);
		try {
			await action();
			reloadMembers();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Page
			title="Who can touch what"
			description="Signing in is settled by g1 auth. This decides what happens next: a role sets the ceiling, and grants below manager pick out the exact accounts, agents and proxies a person works with."
			actions={myRole ? <Badge variant="info">{label(myRole)}</Badge> : null}
		>
			<ol className="ladder">
				{LADDER.map((rung, i) => (
					<li
						key={rung.role}
						className={rung.role === myRole ? "rung rung-you" : "rung"}
						style={{ "--rung": i + 1 } as React.CSSProperties}
					>
						<span className="rung-name">{label(rung.role)}</span>
						<span className="rung-holds">{rung.holds}</span>
					</li>
				))}
			</ol>

			{error && <InfoBanner variant="danger" title={error} />}
			{members.state === "failed" &&
				(() => {
					// By the status: «visible to admins only» is a 403's sentence and no other's.
					const f = readFailure(members.status, {
						what: "Members",
						forbidden: "Members are visible to admins only.",
					});
					return (
						<InfoBanner variant={f.variant} title={f.title}>
							{members.why}
						</InfoBanner>
					);
				})()}

			<Table<Member>
				columns={[
					{
						id: "who",
						header: "Member",
						accessor: (m) => (
							<>
								<div className="font-medium text-content">{m.email ?? m.displayName ?? "—"}</div>
								<div className="font-mono text-xs text-muted">{m.userId}</div>
							</>
						),
					},
					{
						id: "role",
						header: "Role",
						accessor: (m) => (
							<Select
								value={m.role}
								disabled={busy || !canAssign.includes(m.role)}
								onChange={(e) =>
									void run(() => api.setMember(m.userId, { role: e.target.value as Role }))
								}
								options={(canAssign.includes(m.role) ? canAssign : [...canAssign, m.role]).map(
									(r) => ({ value: r, label: label(r) }),
								)}
							/>
						),
					},
					{ id: "holds", header: "Granted", accessor: (m) => summarise(m) },
					{
						id: "actions",
						header: "",
						accessor: (m) => (
							<div className="flex gap-2">
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setEditing(editing === m.userId ? null : m.userId)}
								>
									{editing === m.userId ? "Close" : "Edit access"}
								</Button>
								<Button
									variant="ghost"
									size="sm"
									disabled={busy}
									onClick={() => void run(() => api.removeMember(m.userId))}
								>
									Remove
								</Button>
							</div>
						),
					},
				]}
				data={members.state === "ready" ? members.data.members : []}
				keyExtractor={(m) => m.id}
				failed={members.state === "failed"}
				loading={members.state === "loading"}
				emptyMessage="Nobody is seated yet. Add the first member below — until then, only platform admins can get in."
			/>

			{editing && members.state === "ready" && (
				<GrantEditor
					key={editing}
					member={members.data.members.find((m) => m.userId === editing)}
					options={options}
					busy={busy}
					onSave={(payload) => void run(() => api.setGrants(editing, payload))}
				/>
			)}

			<SeatForm
				canAssign={canAssign}
				busy={busy}
				onSeat={(userId, role, email) => void run(() => api.setMember(userId, { role, email }))}
			/>
		</Page>
	);
}

function GrantEditor({
	member,
	options,
	busy,
	onSave,
}: {
	member?: Member;
	options: Record<EntityType, Array<{ id: string; label: string }>>;
	busy: boolean;
	onSave: (p: { entity_type: EntityType; permission: Permission; entity_ids: string[] }) => void;
}) {
	const [type, setType] = useState<EntityType>("tiktok_accounts");
	const [permission, setPermission] = useState<Permission>("read");
	if (!member) return null;

	const selected = new Set(
		member.grants
			.filter((g) => g.entityType === type && g.permission === permission)
			.map((g) => g.entityId),
	);
	const list = options[type];

	function toggle(id: string) {
		const next = new Set(selected);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		onSave({ entity_type: type, permission, entity_ids: [...next] });
	}

	return (
		<Card className="mt-6 p-5">
			<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">
				Access for {member.email ?? member.userId}
				{(member.role === "admin" || member.role === "super_admin") &&
					" — holds everything by role; grants below are ignored"}
			</p>
			<div className="flex flex-wrap items-end gap-3">
				<Select
					label="Type"
					value={type}
					onChange={(e) => setType(e.target.value as EntityType)}
					options={TYPES.map((t) => ({ value: t.type, label: t.label }))}
				/>
				<Select
					label="Permission"
					value={permission}
					onChange={(e) => setPermission(e.target.value as Permission)}
					options={[
						{ value: "read", label: "Can view" },
						{ value: "write", label: "Can change" },
					]}
				/>
			</div>

			<label className="mt-4 flex cursor-pointer items-start gap-2 border-b border-line pb-3 text-sm font-medium">
				<input
					type="checkbox"
					className="mt-1"
					checked={selected.has("*")}
					disabled={busy}
					onChange={() => toggle("*")}
				/>
				<span>Everything of this type, including anything added later</span>
			</label>

			{list.length === 0 ? (
				<p className="mt-4 text-sm text-muted">Nothing of this type exists yet to grant.</p>
			) : (
				<div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-2">
					{list.map((o) => (
						<label key={o.id} className="flex cursor-pointer items-start gap-2 text-sm">
							<input
								type="checkbox"
								className="mt-1"
								checked={selected.has(o.id)}
								disabled={busy || selected.has("*")}
								onChange={() => toggle(o.id)}
							/>
							<span>{o.label}</span>
						</label>
					))}
				</div>
			)}
			<p className="mt-4 text-xs text-muted">
				Pick as many as needed — each one is saved as you tick it.
			</p>
		</Card>
	);
}

function SeatForm({
	canAssign,
	busy,
	onSeat,
}: {
	canAssign: Role[];
	busy: boolean;
	onSeat: (userId: string, role: Role, email: string) => void;
}) {
	const [userId, setUserId] = useState("");
	const [email, setEmail] = useState("");
	const [role, setRole] = useState<Role>("user");
	if (canAssign.length === 0) return null;

	return (
		<Card className="mt-6 p-5">
			<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">Seat a member</p>
			<form
				className="flex flex-wrap items-end gap-3"
				onSubmit={(e) => {
					e.preventDefault();
					if (userId.trim()) onSeat(userId.trim(), role, email.trim());
					setUserId("");
					setEmail("");
				}}
			>
				<Input
					label="g1-auth user id"
					value={userId}
					onChange={(e) => setUserId(e.target.value)}
					helperText="The staff id from admin.g1.network"
				/>
				<Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
				<Select
					label="Role"
					value={role}
					onChange={(e) => setRole(e.target.value as Role)}
					options={canAssign.map((r) => ({ value: r, label: label(r) }))}
				/>
				<Button type="submit" loading={busy} disabled={!userId.trim()}>
					Add
				</Button>
			</form>
		</Card>
	);
}

function label(role: Role): string {
	return role === "super_admin"
		? "Super admin"
		: role === "admin"
			? "Admin"
			: role === "manager"
				? "Manager"
				: "User";
}

function summarise(m: Member): string {
	if (m.role === "admin" || m.role === "super_admin") return "Everything, by role";
	if (m.grants.length === 0) return "Nothing yet";
	return TYPES.map((t) => {
		const ids = new Set(m.grants.filter((g) => g.entityType === t.type).map((g) => g.entityId));
		if (ids.size === 0) return null;
		return ids.has("*") ? `all ${t.label.toLowerCase()}` : `${ids.size} ${t.label.toLowerCase()}`;
	})
		.filter(Boolean)
		.join(" · ");
}
